import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import usePortalPopover from './usePortalPopover';
import { gradeColor } from '../utils/gradeColor';
import {
  ComboMenu,
  GradePillShell,
  GradePillValue,
  GradePillGrades,
  GradeChip,
  GradePillChevron,
} from '../styles/CustomerSpecsIndexStyles';

// Split-pill grade control: a value zone + a grade-chip tray that opens a checkbox
// popover (built on usePortalPopover, NOT TypeaheadSelect — multi-select, no text
// input). Enforces >=1 grade whenever the spec has grades (None is only meaningful
// at G=0); "All Grades" is a select-all shortcut and a no-op when already all.
//
// Props: valueLabel (left zone text), grades (spec grade names, spec order),
//        selected (this entry's assigned names), onChange(nextNames).
//
// Bag-label props (styles tier only; both null/undefined elsewhere so Label/Method
// instances render exactly as before):
//   bagContext  { options:[{label, matched}], autoPick:{label|null, reason},
//                 selection (wire: null|'NONE'|[ids]), invalid } | null.
//               Options are the customer-visible labels that PASS this style's rules,
//               in canonical LabelName order, plus any already-pinned label that no
//               longer matches (kept so it can still be unchecked). `invalid` red-
//               outlines the pill — an unresolved bag-label tie caught at save.
//   onBagLabelsChange(nextWire)
// With a context the popover gains a second column (Default + label checkboxes) and
// the pill tray appends the effective labels after a divider.

const ROW_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 10px',
  cursor: 'pointer',
  fontSize: '0.85rem',
  userSelect: 'none',
};

const BAG_NOTE_STYLE = {
  padding: '4px 10px',
  fontSize: '0.8rem',
  fontStyle: 'italic',
  color: 'var(--shadow-a55)',
};

const BAG_EMPTY_CHIP_STYLE = {
  fontSize: '0.78rem',
  fontStyle: 'italic',
  color: 'var(--shadow-a55)',
  whiteSpace: 'nowrap',
};

export default function GradeMultiSelect({ valueLabel, grades = [], selected = [], onChange, bagContext = null, onBagLabelsChange }) {
  const { open, setOpen, anchorRef, menuRef, menuStyle } = usePortalPopover();
  const G = grades.length;
  const hasBagContext = !!bagContext;
  // The bag column must stay reachable at G=0 (R5) without loosening the >=1-grade rule.
  const interactive = G > 0 || hasBagContext;
  // Intersect against the spec's current grades so a stale name left in `selected`
  // during the prune/reTier interim can't miscount the >=1 guard or the ALL test (E1).
  const valid = selected.filter((g) => grades.includes(g));
  const selSet = new Set(valid);
  const isAll = G > 0 && valid.length === G;

  // Escape closes (focus may be on a checkbox or the trigger).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  // Re-project the next selection into spec order before emitting.
  const emit = (nextSet) => onChange?.(grades.filter((g) => nextSet.has(g)));
  const toggleAll = () => { if (!isAll) emit(new Set(grades)); }; // no-op when all (can't clear to None at G>=1)
  const toggleOne = (name) => {
    const next = new Set(selSet);
    if (next.has(name)) {
      if (next.size <= 1) return; // enforce >=1: can't uncheck the last grade
      next.delete(name);
    } else {
      next.add(name);
    }
    emit(next);
  };

  // ── Bag-label selection state (wire form) ──────────────────────────────────
  const bagSelection = hasBagContext ? (bagContext.selection ?? null) : null;
  const isBagDefault = hasBagContext && bagSelection === null;
  const bagPins = Array.isArray(bagSelection) ? bagSelection.map(Number) : [];
  const bagAutoId = bagContext?.autoPick?.label ? Number(bagContext.autoPick.label.UniqueID) : null;
  const bagReason = bagContext?.autoPick?.reason;

  const emitBag = (next) => onBagLabelsChange?.(next);
  // Default checked ⇔ selection === null; unchecking it is the manual-clear gesture.
  const toggleBagDefault = () => emitBag(isBagDefault ? 'NONE' : null);
  // Manual edits drop "ghost" pins (labels since deactivated/rescoped — Decision 7
  // cascade); otherwise an invisible ghost keeps 'NONE' unreachable and resurfaces
  // if the label is later reactivated.
  const visibleBagIds = new Set((bagContext?.options || []).map((o) => Number(o.label.UniqueID)));
  const toggleBagLabel = (id) => {
    const n = Number(id);
    if (isBagDefault) {
      // Leaving Default pins the current auto pick first (when one exists), deduped.
      emitBag(bagAutoId != null && bagAutoId !== n ? [bagAutoId, n] : [n]);
      return;
    }
    const livePins = bagPins.filter((x) => visibleBagIds.has(x));
    if (livePins.includes(n)) {
      const next = livePins.filter((x) => x !== n);
      emitBag(next.length ? next : 'NONE');
      return;
    }
    emitBag([...livePins, n]);
  };

  // Effective labels for the pill tray — resolveForStyle semantics applied to the
  // context's pre-filtered visible options (pins drift-drop, order stays canonical).
  const effectiveBagLabels = (() => {
    if (!hasBagContext) return [];
    if (bagSelection === 'NONE') return [];
    if (Array.isArray(bagSelection)) {
      const ids = new Set(bagPins);
      return bagContext.options.filter((o) => ids.has(Number(o.label.UniqueID))).map((o) => o.label);
    }
    return bagContext.autoPick?.label ? [bagContext.autoPick.label] : [];
  })();

  const chips = () => {
    if (valid.length === 0) return <GradeChip $neutral>None</GradeChip>;
    if (isAll) return <GradeChip $neutral>ALL</GradeChip>;
    return grades
      .filter((g) => selSet.has(g))
      .map((name) => {
        const c = gradeColor(name);
        return <GradeChip key={name} $bg={c.bg} $border={c.border}>{name}</GradeChip>;
      });
  };

  const bagChips = hasBagContext ? (
    <>
      <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--shadow-a15)' }} />
      {effectiveBagLabels.length > 0 ? (
        effectiveBagLabels.map((l) => (
          <GradeChip key={`bag-${l.UniqueID}`} $neutral>{l.LabelName}</GradeChip>
        ))
      ) : (
        <span style={BAG_EMPTY_CHIP_STYLE}>
          {isBagDefault && bagReason === 'tie' ? 'Unresolved' : 'No Bag Label'}
        </span>
      )}
    </>
  ) : null;

  const gradesColumn = (
    <>
      <label style={{ ...ROW_STYLE, fontWeight: 700, borderBottom: '1px solid var(--shadow-a08)' }}>
        <input type="checkbox" checked={isAll} onChange={toggleAll} />
        <span>All Grades</span>
      </label>
      {grades.map((name) => {
        const c = gradeColor(name);
        return (
          <label key={name} style={ROW_STYLE}>
            <input type="checkbox" checked={selSet.has(name)} onChange={() => toggleOne(name)} />
            <span style={{ width: 12, height: 12, borderRadius: 3, background: c.bg, flex: '0 0 auto' }} />
            <span>{name}</span>
          </label>
        );
      })}
    </>
  );

  const bagRow = (o) => {
    const id = Number(o.label.UniqueID);
    return (
      <label key={id} style={ROW_STYLE}>
        <input type="checkbox" checked={bagPins.includes(id)} onChange={() => toggleBagLabel(id)} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label.LabelName}</span>
        {isBagDefault && bagReason === 'auto' && bagAutoId === id ? (
          <span style={{ fontSize: '0.68rem', fontWeight: 800, padding: '1px 6px', borderRadius: 999, background: 'var(--grey-200)', color: 'var(--grey-700)', border: '1px solid var(--shadow-a08)' }}>
            auto
          </span>
        ) : null}
      </label>
    );
  };

  // Every option here already passes the rules (SpecFormFields filters them); the only
  // possible non-matching row is a pre-existing pin kept so it can be unchecked, and it
  // renders like any other row. No matched/off-rule split.
  const bagColumn = hasBagContext ? (
    <div>
      <label style={{ ...ROW_STYLE, fontWeight: 700, borderBottom: '1px solid var(--shadow-a08)' }}>
        <input type="checkbox" checked={isBagDefault} onChange={toggleBagDefault} />
        <span>Default</span>
      </label>
      {isBagDefault && bagReason === 'none' ? (
        <div style={BAG_NOTE_STYLE}>No Applicable Entries Found</div>
      ) : null}
      {isBagDefault && bagReason === 'tie' ? (
        <div style={BAG_NOTE_STYLE}>Tied Entries — Select One</div>
      ) : null}
      {bagContext.options.map(bagRow)}
    </div>
  ) : null;

  const menu = open && interactive ? (
    <ComboMenu
      ref={menuRef}
      role="group"
      aria-label={hasBagContext ? 'Grades and bag labels' : 'Grades'}
      // menuStyle pins width to the anchor; the two-column body needs its own (R4,
      // StatusPicker precedent). ComboMenu's base CSS carries `left: 0; right: 0` —
      // an explicit pixel width over-constrains the box so `right` is ignored, but
      // `width: 'auto'` does not, so the bag variant MUST null out `right` or it
      // stretches to the viewport edge.
      style={hasBagContext
        ? {
          ...menuStyle,
          width: 'auto',
          right: 'auto',
          minWidth: G > 0 ? 440 : 240,
          maxWidth: 'min(620px, calc(100vw - 20px))',
          overflowY: 'auto',
        }
        : { ...menuStyle, overflowY: 'auto' }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {hasBagContext ? (
        // At G=0 the grades column would be a lone dead "All Grades" checkbox
        // (toggleAll emits an empty set there) — show only the bag column.
        G > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 8 }}>
            <div>{gradesColumn}</div>
            <div style={{ borderLeft: '1px solid var(--shadow-a08)', paddingLeft: 8 }}>{bagColumn}</div>
          </div>
        ) : bagColumn
      ) : gradesColumn}
    </ComboMenu>
  ) : null;

  return (
    <>
      <GradePillShell
        ref={anchorRef}
        type="button"
        $disabled={!interactive}
        $invalid={!!bagContext?.invalid}
        disabled={!interactive}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => { if (interactive) setOpen((o) => !o); }}
      >
        <GradePillValue>{valueLabel}</GradePillValue>
        <GradePillGrades>{chips()}{bagChips}</GradePillGrades>
        {interactive ? <GradePillChevron><ChevronDown size={14} /></GradePillChevron> : null}
      </GradePillShell>
      {menu ? createPortal(menu, document.body) : null}
    </>
  );
}
